/**
 * Cliente HTTP del Greenie Board — Fase 5g.3 + 5g.5d.
 *
 *   GET    /api/greenie-board               público (read-only)
 *   PATCH  /api/greenie-board/landings/:id  admin — editar un apontaje
 *   POST   /api/greenie-board/landings      admin — crear un apontaje
 *   DELETE /api/greenie-board/landings/:id  admin — borrar un apontaje
 *
 * Todas las llamadas devuelven la misma unión discriminada
 * `{ ok, data } | { ok:false, message }` que el resto de clientes.
 */

import type {
  GreenieBoardResponse,
  GreenieLandingCreateBody,
  GreenieLandingEditBody,
  GreenieLandingMutationResult,
} from '@51esc/shared';

/** Resultado de una llamada al API del Greenie Board. */
export type GreenieApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; message: string };

/**
 * `GET /api/greenie-board` — tablero completo de apontajes.
 *
 * Sin paginación: con 10-30 pilotos y unas docenas de apontajes por
 * piloto el payload es modesto (ver JSDoc del endpoint).
 */
export async function fetchGreenieBoard(): Promise<
  GreenieApiResult<GreenieBoardResponse>
> {
  let res: Response;
  try {
    res = await fetch('/api/greenie-board', { credentials: 'include' });
  } catch (err) {
    console.error('[greenie-board] fallo de red:', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* respuesta sin JSON — se trata como error genérico abajo */
  }

  if (!res.ok) {
    const e = (body ?? {}) as { error?: string };
    return { ok: false, message: e.error ?? `Error ${String(res.status)}` };
  }

  return { ok: true, data: body as GreenieBoardResponse };
}

// ─── Mutaciones admin (Fase 5g.5d) ─────────────────────────────────────

/** Llamada común a las mutaciones admin de apontajes. */
async function mutate(
  method: 'PATCH' | 'POST' | 'DELETE',
  url: string,
  body?: unknown,
): Promise<GreenieApiResult<GreenieLandingMutationResult>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers:
        body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    console.error('[greenie-board] fallo de red:', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }

  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* respuesta sin JSON */
  }

  if (!res.ok) {
    const e = (parsed ?? {}) as { error?: string };
    return { ok: false, message: e.error ?? `Error ${String(res.status)}` };
  }

  return {
    ok: true,
    data: (parsed ?? { ok: true }) as GreenieLandingMutationResult,
  };
}

const LANDINGS = '/api/greenie-board/landings';

/** `PATCH /landings/:id` — edita grade / cable / comentario de un apontaje. */
export function patchLanding(
  id: string,
  body: GreenieLandingEditBody,
): Promise<GreenieApiResult<GreenieLandingMutationResult>> {
  return mutate('PATCH', `${LANDINGS}/${encodeURIComponent(id)}`, body);
}

/** `POST /landings` — crea un apontaje adyacente a otro (el "split"). */
export function createLanding(
  body: GreenieLandingCreateBody,
): Promise<GreenieApiResult<GreenieLandingMutationResult>> {
  return mutate('POST', LANDINGS, body);
}

/** `DELETE /landings/:id` — borra un apontaje. */
export function deleteLanding(
  id: string,
): Promise<GreenieApiResult<GreenieLandingMutationResult>> {
  return mutate('DELETE', `${LANDINGS}/${encodeURIComponent(id)}`);
}
